import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";

import { LoadboardModal, LoadModalDetails, ModalDisclaimer } from "@/components/dataPages/loadboard";
import { TranslatedText } from "@/components/common/text";
import { API } from "@/services/API";
import { getLoadId } from "@/utils/load";
import { getNumberWithCommas } from "@/utils/text";
import { fetchAllOffers, updateOfferStatus } from "@/state/offersSlice";
import { useOffersRefresh } from "@/contexts/OffersRefreshContext";
import { updatePrevOffers } from "@/hooks/useOfferStatusNotifications";

export const LoadboardCancelModal = ({ offer, open, onOpenChange, isDisabled, reasonType, buttonClassName }) => {
    const dispatch = useDispatch();
    const resetOffersInterval = useOffersRefresh();
    const { quotes, bookings } = useSelector((state) => state.offers);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const idToShow = load.externalLoadId || getLoadId(offer.loadBoardItem.uniqueId);
    const isQuote = offer.type === "quote";

    const handleConfirmCancel = () => {
        if (!isLoading) {
            setIsLoading(true);
            const endpoint = isQuote ? "/parade/quote/cancel" : "/parade/book/drop";
            const reason = { reason: reasonType === "myOffers" ? "Cancel from my offers" : "Cancel from table" };
            const payload = isQuote ? { quoteId: offer.id, reason } : { bookingId: offer.id, reason };

            API.post(endpoint, payload)
                .then(() => {
                    // Optimistically update the offer status and increment counter
                    // Note: Backend returns EXPIRED for cancelled quotes, CANCELLED for cancelled bookings
                    dispatch(
                        updateOfferStatus({
                            offerId: offer.id,
                            offerType: isQuote ? "quote" : "booking",
                            newStatus: isQuote ? "EXPIRED" : "CANCELLED",
                            incrementCounter: true,
                        }),
                    );

                    // Update the notification hook's ref to prevent "broker declined" toast
                    // This syncs the ref with the optimistically updated state
                    const updatedQuotes = isQuote
                        ? quotes.data.map((q) => (q.id === offer.id ? { ...q, status: "EXPIRED" } : q))
                        : quotes.data;
                    const updatedBookings = !isQuote
                        ? bookings.data.map((b) => (b.id === offer.id ? { ...b, status: "CANCELLED" } : b))
                        : bookings.data;
                    updatePrevOffers(updatedQuotes, updatedBookings);

                    onOpenChange(false);
                    setError(null);
                    if (resetOffersInterval) {
                        resetOffersInterval();
                    }
                    dispatch(fetchAllOffers({ isAutoRefresh: true }));
                    toast.info(`Load ${idToShow} offer cancelled successfully!`);
                })
                .catch((err) => {
                    console.error("Failed to cancel offer", err);
                    setError(typeof err === "string" ? err : "Cancel failed.");
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    return (
        <LoadboardModal
            title={`Cancel ${isQuote ? "bid" : "booking"}?`}
            buttonClassName={buttonClassName || "text-cm-p-on-container-red font-medium text-sm/[22px]"}
            buttonDisabled={isDisabled}
            buttonChildren={<TranslatedText text={`Cancel ${isQuote ? "bid" : "booking"}`} />}
            open={open}
            onOpenChange={onOpenChange}
            usePortal={reasonType !== "myOffers"}
        >
            <LoadModalDetails load={offer.loadBoardItem} quote={offer.offerRate ? getNumberWithCommas(offer.offerRate, true) : null} />
            <ModalDisclaimer text="Disclaimer message to think of if needed to be added here, or instruction what’s gonna happen after Confirm." />
            <button
                type="button"
                onClick={handleConfirmCancel}
                disabled={isLoading}
                className="mt-3 bg-cm-p-on-container-red text-cm-p-surface w-full h-11 rounded-lg disabled:opacity-30 text-sm/[22px] font-medium inline-flex items-center justify-center gap-1"
            >
                <TranslatedText text={`Yes, cancel ${isQuote ? "bid" : "booking"}`} />
            </button>
            {error ? <div className="text-cm-error pt-3">{error}</div> : null}
        </LoadboardModal>
    );
};
